/**
 * workoutApi.js — client for our own backend (Vercel serverless /api/*).
 * Attaches Firebase ID token as Bearer. Falls back gracefully (throws ApiError)
 * so callers can keep localStorage behaviour when the backend is unreachable
 * or the user isn't signed in with Firebase.
 */
import { getFirebaseIdToken } from './firebase';

const BASE = import.meta.env.VITE_WORKOUT_API_BASE || '/api';

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** True when the backend is usable (Firebase signed-in user present). */
export async function isBackendReady() {
  try { return !!(await getFirebaseIdToken()); } catch { return false; }
}

async function request(path, { method = 'GET', body, query, timeoutMs = 15000 } = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new ApiError(401, 'no_firebase_token', 'Not signed in');

  let url = `${BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams(
      Object.entries(query).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])
    ).toString();
    if (qs) url += `?${qs}`;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body != null ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }

  let data = null;
  try { data = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    throw new ApiError(res.status, data?.error, data?.message);
  }
  return data;
}

// ── Profile ──────────────────────────────────────────────────────────────────
export const getProfile = (authMethod) => request('/profile', { query: { authMethod } });
export const updateProfile = (patch) => request('/profile', { method: 'PUT', body: patch });

// ── Workout ──────────────────────────────────────────────────────────────────
export const saveWorkout = (session) => request('/workout/complete', { method: 'POST', body: session });
export const getWorkoutHistory = (limit = 50) => request('/workout/history', { query: { limit } });

// ── Energy ───────────────────────────────────────────────────────────────────
export const getEnergy = () => request('/energy');
export const syncEnergy = (snapshot) => request('/energy', { method: 'PUT', body: snapshot });

// ── Leaderboard ──────────────────────────────────────────────────────────────
export const getLeaderboard = (scope = 'global', limit = 50) =>
  request('/leaderboard', { query: { scope, limit } });

// ── Referral ─────────────────────────────────────────────────────────────────
export const applyReferral = (code) => request('/referral', { method: 'POST', body: { action: 'apply', code } });
export const getReferralStatus = () => request('/referral', { method: 'POST', body: { action: 'status' } });

// ── Account ──────────────────────────────────────────────────────────────────
/** Permanently delete the signed-in user's server data + Firebase auth user. */
export const deleteAccount = () => request('/account/delete', { method: 'DELETE' });

// ── Health ───────────────────────────────────────────────────────────────────
export const health = () => fetch(`${BASE}/health`).then((r) => r.json());
